import { Slider, Typography, Stack, useTheme, IconButton } from '@mui/material';
import { SGTooltip } from 'components/core';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ValueFormat } from 'types/shared';
import {
  formatAsCompactNumber,
  transformLogToValue,
  transformValueToLog,
} from 'util/shared';
import debounce from 'lodash/debounce';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Mark } from '@mui/material/Slider/useSlider.types';

interface SliderFilterProps {
  header: string;
  description?: string;
  name: string;
  min: number;
  max: number;
  marks?: Mark[];
  value: number | number[];
  onChange: (newValue: number | number[]) => void;
  valueFormat: ValueFormat;
  viewOnly?: boolean;
  disabled?: boolean;
  useLogScale?: boolean;
  displayValueFormatter?: (value: number | number[]) => string;
  step?: number | null;
  updateOnSlide?: boolean;
  showStepButtons?: boolean;
}

const getFormattedValueLabel = (
  value: number,
  valueFormat: ValueFormat = 'number',
) => {
  if (valueFormat === 'currency') {
    return `$${formatAsCompactNumber(value)}`;
  } else if (valueFormat === 'date') {
    const roundedVal = Math.round(value);
    return roundedVal === 0
      ? 'Today'
      : `${roundedVal} day${roundedVal > 1 ? 's' : ''}`;
  } else {
    return formatAsCompactNumber(value);
  }
};

const SliderFilter = ({
  header,
  description,
  name,
  min,
  max,
  marks,
  value,
  valueFormat = 'number',
  onChange,
  displayValueFormatter,
  step = 1,
  viewOnly = false,
  disabled = false,
  useLogScale = false, // Default to linear scale
  updateOnSlide = false, // Default to false
  showStepButtons = false,
}: SliderFilterProps) => {
  const theme = useTheme();

  // Initialize localValue as an array if value is an array
  const [localValue, setLocalValue] = useState<number | number[]>(
    Array.isArray(value) ? value : [min, max],
  );

  // Create debounced onChange function
  const debouncedOnChange = useMemo(
    () =>
      debounce((newValue: number | number[]) => {
        onChange(newValue);
      }, 100),
    [onChange],
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  // Synchronize localValue with the value prop
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const getSliderInfo = useCallback(() => {
    if (displayValueFormatter != null) {
      return displayValueFormatter(localValue);
    }

    if (!Array.isArray(localValue)) {
      return localValue === 0 && valueFormat === 'currency'
        ? 'All Premiums'
        : `More than ${getFormattedValueLabel(localValue, valueFormat)}`;
    }

    const currentValue = Array.isArray(localValue) ? localValue : [min, max];

    if (currentValue[0] === min && currentValue[1] === max) {
      return `All ${name}s`;
    } else if (currentValue[0] > min && currentValue[1] === max) {
      return `More than ${getFormattedValueLabel(
        currentValue[0],
        valueFormat,
      )}`;
    } else if (currentValue[0] === min && currentValue[1] < max) {
      return `Less than ${getFormattedValueLabel(
        currentValue[1],
        valueFormat,
      )}`;
    }

    return `${getFormattedValueLabel(
      currentValue[0],
      valueFormat,
    )} to ${getFormattedValueLabel(currentValue[1], valueFormat)}`;
  }, [name, localValue, min, max, valueFormat, displayValueFormatter]);

  const handleChange = useCallback(
    (
      _event: Event | SyntheticEvent<Element, Event>,
      newValue: number | number[],
    ) => {
      if (viewOnly) {
        return;
      }

      let transformedValue: number | number[];

      if (useLogScale) {
        if (Array.isArray(newValue)) {
          transformedValue = [
            transformLogToValue(newValue[0], min, max),
            transformLogToValue(newValue[1], min, max),
          ];
        } else {
          transformedValue = transformLogToValue(newValue, min, max);
        }
      } else {
        transformedValue = newValue;
      }

      setLocalValue(transformedValue);

      // If updateOnSlide is true, debounce the onChange call
      if (updateOnSlide) {
        debouncedOnChange(transformedValue);
      }
    },
    [viewOnly, useLogScale, min, max, updateOnSlide, debouncedOnChange],
  );

  const handleChangeCommitted = useCallback(
    (
      _event: Event | SyntheticEvent<Element, Event>,
      newValue: number | number[],
    ) => {
      if (viewOnly || updateOnSlide) {
        return; // Don't call onChange on commit if updateOnSlide is true
      }

      if (useLogScale) {
        if (Array.isArray(newValue)) {
          onChange([
            transformLogToValue(newValue[0], min, max),
            transformLogToValue(newValue[1], min, max),
          ]);
        } else {
          onChange(transformLogToValue(newValue, min, max));
        }
      } else {
        onChange(newValue);
      }
    },
    [onChange, min, max, useLogScale, viewOnly, updateOnSlide],
  );

  const handleStepDown = useCallback(() => {
    if (viewOnly || disabled || step == null) {
      return;
    }

    const currentVal = Array.isArray(localValue) ? localValue[0] : localValue;
    const newValue = Math.max(min, currentVal - step);

    if (Array.isArray(localValue)) {
      const newArrayValue = [newValue, localValue[1]];
      setLocalValue(newArrayValue);
      onChange(newArrayValue);
    } else {
      setLocalValue(newValue);
      onChange(newValue);
    }
  }, [localValue, min, step, viewOnly, disabled, onChange]);

  const handleStepUp = useCallback(() => {
    if (viewOnly || disabled || step == null) {
      return;
    }

    const currentVal = Array.isArray(localValue) ? localValue[1] : localValue;
    const newValue = Math.min(max, currentVal + step);

    if (Array.isArray(localValue)) {
      const newArrayValue = [localValue[0], newValue];
      setLocalValue(newArrayValue);
      onChange(newArrayValue);
    } else {
      setLocalValue(newValue);
      onChange(newValue);
    }
  }, [localValue, max, step, viewOnly, disabled, onChange]);

  const sliderValue = useMemo(() => {
    return useLogScale
      ? Array.isArray(localValue)
        ? [
            transformValueToLog(localValue[0], min, max),
            transformValueToLog(localValue[1], min, max),
          ]
        : transformValueToLog(localValue, min, max)
      : localValue;
  }, [localValue, useLogScale, min, max]);

  const disabledState = viewOnly || disabled;

  return (
    <Stack sx={{ gap: 1 }}>
      <Stack
        sx={{
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <SGTooltip title={description}>
          <Typography
            sx={{
              textTransform: 'uppercase',
              color: disabledState
                ? theme.palette.text.disabled
                : theme.palette.text.secondary,
            }}
          >
            {header}
          </Typography>
        </SGTooltip>

        <Typography
          sx={{
            color: disabledState
              ? theme.palette.text.disabled
              : theme.palette.text.primary,
            fontSize: { xs: 11, md: 13 },
            textAlign: 'end',
          }}
        >
          {getSliderInfo()}
        </Typography>
      </Stack>
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: showStepButtons ? 1 : 0,
        }}
      >
        {showStepButtons && step != null && (
          <IconButton
            size="small"
            color="primary"
            onClick={handleStepDown}
            disabled={disabledState}
            sx={{
              width: 20,
              height: 20,
              color: disabledState
                ? theme.palette.text.disabled
                : theme.palette.text.secondary,
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
        )}
        <Slider
          value={sliderValue}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
          min={min}
          max={max}
          step={step}
          marks={marks}
          disabled={disabledState}
          sx={{
            color: disabledState
              ? theme.palette.action.disabled // Discolored when disabled
              : theme.palette.sgGreen,
            height: 8,
            flex: 1,
            '& .MuiSlider-thumb': {
              cursor: disabledState ? 'default' : 'ew-resize', // Disable cursor change in viewOnly mode
              height: disabledState ? 0 : 20, // Hide thumb in view-only mode
              width: disabledState ? 0 : 8, // Hide thumb in view-only mode
              borderRadius: '4px',
              '&:focus, &:hover, &.Mui-active': {
                boxShadow: 'inherit',
              },
            },
            '& .MuiSlider-track': {
              border: 'none',
              backgroundColor: disabledState
                ? theme.palette.action.disabled // Discolored track
                : theme.palette.sgGreen,
            },
            '& .MuiSlider-rail': {
              opacity: disabledState ? 0.3 : 0.5, // Lighter rail in view-only mode
            },
          }}
        />
        {showStepButtons && step != null && (
          <IconButton
            size="small"
            color="primary"
            onClick={handleStepUp}
            disabled={disabledState}
            sx={{
              width: 20,
              height: 20,
              color: disabledState
                ? theme.palette.text.disabled
                : theme.palette.text.secondary,
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

export default SliderFilter;
